import { addEdge, applyEdgeChanges, applyNodeChanges } from "@xyflow/react"
import { create } from "zustand"

import type { Edge, Node, NodeChange, OnConnect, OnEdgesChange, OnNodesChange } from "@xyflow/react"

import { cleanupRemovedAttachmentNodes } from "../utils/workflow-attachment-utils"
import { normalizeWorkflowEdges } from "../utils/workflow-edge-utils"

const DEFAULT_NODES: Node[] = [
  { id: "start-1", type: "start", position: { x: 0, y: 0 }, data: { label: "Start" } },
  { id: "end-1", type: "end", position: { x: 300, y: 0 }, data: { label: "End" } },
]

const MAX_HISTORY_SIZE = 50

// Test panel width constants
const DEFAULT_TEST_PANEL_WIDTH = 384
const MIN_TEST_PANEL_WIDTH = 320
const MAX_TEST_PANEL_WIDTH = 600

function clampTestPanelWidth(width: number): number {
  return Math.min(MAX_TEST_PANEL_WIDTH, Math.max(MIN_TEST_PANEL_WIDTH, width))
}

// A node may legitimately re-execute (and thus move from completed back to
// executing) only when it participates in a loop: either the loop container
// itself (re-armed after each iteration) or one of its children (which run once
// per iteration). Every other node is terminal once completed — a stray or
// out-of-order activity must not move it back to executing (avoids spinner
// flicker on already-finished nodes).
function isLoopReExecutableNode(nodeId: string, nodes: Node[]): boolean {
  const node = nodes.find((candidate) => candidate.id === nodeId)
  if (!node) {
    // Unknown to the graph (e.g. the activity references a synthetic id): be
    // permissive so we don't suppress a legitimate executing state.
    return true
  }
  if (node.type === "loopContainer") return true
  if (!node.parentId) return false
  const parent = nodes.find((candidate) => candidate.id === node.parentId)
  return parent?.type === "loopContainer"
}

function expandLoopContainerRemovalChanges(changes: NodeChange[], nodes: Node[]): NodeChange[] {
  const removedNodeIds = new Set(
    changes.filter((change) => change.type === "remove").map((change) => change.id)
  )
  if (removedNodeIds.size === 0) return changes

  const removedLoopIds = new Set(
    nodes
      .filter((node) => node.type === "loopContainer" && removedNodeIds.has(node.id))
      .map((node) => node.id)
  )
  if (removedLoopIds.size === 0) return changes

  let expanded = false
  do {
    expanded = false
    for (const node of nodes) {
      if (node.parentId && removedLoopIds.has(node.parentId) && !removedNodeIds.has(node.id)) {
        removedNodeIds.add(node.id)
        if (node.type === "loopContainer") {
          removedLoopIds.add(node.id)
        }
        expanded = true
      }
    }
  } while (expanded)

  const originalChangeIds = new Set(
    changes.flatMap((change) => ("id" in change ? [change.id] : []))
  )
  const childRemovalChanges: NodeChange[] = [...removedNodeIds]
    .filter((nodeId) => !originalChangeIds.has(nodeId))
    .map((nodeId) => ({ id: nodeId, type: "remove" }))

  return childRemovalChanges.length > 0 ? [...changes, ...childRemovalChanges] : changes
}

type HistoryEntry = {
  nodes: Node[]
  edges: Edge[]
}

export type ExecutionActivity = {
  nodeId: string
  message: string
  timestamp: number
}

export type WorkflowState = {
  nodes: Node[]
  edges: Edge[]
  // History for undo/redo
  history: HistoryEntry[]
  historyIndex: number
  // Read-only mode
  readOnly: boolean
  // Id of the workflow currently open (null for an unsaved draft). Used by nodes
  // that call workflow-scoped APIs (e.g. the attachment node's upload).
  workflowId: string | null
  // Drag-and-drop state
  dropTargetLoopId: string | null
  // Invalid connection highlight state (for loop boundary violations)
  invalidConnectionLoopId: string | null
  invalidConnectionType: "input" | "output" | null
  // Test run execution state
  isExecuting: boolean
  executingNodeIds: string[]
  completedNodeIds: string[]
  executionOutput: string
  executionError: string | null
  executionActivities: ExecutionActivity[]
  // Node output tracking
  nodeOutputs: Record<string, string>
  trackNodeOutputs: boolean
  // Loop progress tracking
  loopProgress: Record<string, { current: number; total: number }>
  // Test panel width
  testPanelWidth: number
  // Auto-layout request counter
  autoLayoutRequestId: number
  // Actions
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  onConnect: OnConnect
  setNodes: (nodes: Node[]) => void
  setEdges: (edges: Edge[]) => void
  applyGraphUpdate: (nodes: Node[], edges: Edge[]) => void
  loadGraph: (nodes: Node[], edges: Edge[]) => void
  resetGraph: () => void
  updateNodeData: (nodeId: string, data: Record<string, unknown>) => void
  updateEdgeData: (edgeId: string, data: Record<string, unknown>) => void
  setActiveNode: (nodeId: string | null) => void
  setReadOnly: (readOnly: boolean) => void
  setWorkflowId: (workflowId: string | null) => void
  setDropTargetLoopId: (loopId: string | null) => void
  setInvalidConnectionHighlight: (loopId: string | null, type: "input" | "output" | null) => void
  // Undo/Redo
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean
  // Test run execution actions
  startExecution: () => void
  setExecutingNode: (nodeId: string | null) => void
  markNodeCompleted: (nodeId: string) => void
  appendExecutionOutput: (content: string) => void
  setExecutionError: (error: string | null) => void
  addExecutionActivity: (activity: ExecutionActivity) => void
  stopExecution: () => void
  clearExecution: () => void
  // Node output tracking actions
  setNodeOutput: (nodeId: string, output: string) => void
  setTrackNodeOutputs: (enabled: boolean) => void
  // Loop progress tracking actions
  setLoopProgress: (nodeId: string, current: number, total: number) => void
  // Test panel width action
  setTestPanelWidth: (width: number) => void
  // Auto-layout action
  requestAutoLayout: () => void
}

// Helper to save current state to history
const saveToHistory = (state: WorkflowState): Partial<WorkflowState> => {
  const currentEntry: HistoryEntry = {
    nodes: structuredClone(state.nodes),
    edges: structuredClone(state.edges),
  }

  // If we're not at the end of history, truncate the future
  const newHistory = state.history.slice(0, state.historyIndex + 1)
  newHistory.push(currentEntry)

  // Keep history within max size
  if (newHistory.length > MAX_HISTORY_SIZE) {
    newHistory.shift()
  }

  return {
    history: newHistory,
    historyIndex: newHistory.length - 1,
  }
}

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  nodes: DEFAULT_NODES,
  edges: [],
  history: [{ nodes: DEFAULT_NODES, edges: [] }],
  historyIndex: 0,
  readOnly: false,
  workflowId: null,
  dropTargetLoopId: null,
  invalidConnectionLoopId: null,
  invalidConnectionType: null,
  // Test run execution state
  isExecuting: false,
  executingNodeIds: [],
  completedNodeIds: [],
  executionOutput: "",
  executionError: null,
  executionActivities: [],
  // Node output tracking
  nodeOutputs: {},
  trackNodeOutputs: true,
  // Loop progress tracking
  loopProgress: {},
  // Test panel width
  testPanelWidth: DEFAULT_TEST_PANEL_WIDTH,
  // Auto-layout request counter
  autoLayoutRequestId: 0,

  onNodesChange: (changes) => {
    const state = get()
    if (state.readOnly) return
    const expandedChanges = expandLoopContainerRemovalChanges(changes, state.nodes)
    const newNodes = applyNodeChanges(expandedChanges, state.nodes)
    void cleanupRemovedAttachmentNodes(state.nodes, newNodes)

    // Only save to history for significant changes (add/remove), not position
    const isSignificantChange = expandedChanges.some((c) => c.type === "add" || c.type === "remove")

    if (isSignificantChange) {
      set({
        nodes: newNodes,
        ...saveToHistory({ ...state, nodes: newNodes }),
      })
    } else {
      set({ nodes: newNodes })
    }
  },

  onEdgesChange: (changes) => {
    const state = get()
    if (state.readOnly) return
    const newEdges = normalizeWorkflowEdges(applyEdgeChanges(changes, state.edges))

    // Save to history for edge changes
    const isSignificantChange = changes.some((c) => c.type === "add" || c.type === "remove")

    if (isSignificantChange) {
      set({
        edges: newEdges,
        ...saveToHistory({ ...state, edges: newEdges }),
      })
    } else {
      set({ edges: newEdges })
    }
  },

  onConnect: (connection) => {
    const state = get()
    if (state.readOnly) return
    const newEdges = normalizeWorkflowEdges(addEdge(connection, state.edges))
    set({
      edges: newEdges,
      ...saveToHistory({ ...state, edges: newEdges }),
    })
  },

  setNodes: (nodes) => {
    const state = get()
    if (state.readOnly) return
    void cleanupRemovedAttachmentNodes(state.nodes, nodes)
    set({
      nodes,
      ...saveToHistory({ ...state, nodes }),
    })
  },

  setEdges: (edges) => {
    const state = get()
    if (state.readOnly) return
    const normalizedEdges = normalizeWorkflowEdges(edges)
    set({
      edges: normalizedEdges,
      ...saveToHistory({ ...state, edges: normalizedEdges }),
    })
  },

  applyGraphUpdate: (nodes, edges) => {
    const state = get()
    if (state.readOnly) return
    void cleanupRemovedAttachmentNodes(state.nodes, nodes)
    const normalizedEdges = normalizeWorkflowEdges(edges)
    set({
      nodes,
      edges: normalizedEdges,
      ...saveToHistory({ ...state, nodes, edges: normalizedEdges }),
    })
  },

  loadGraph: (nodes, edges) => {
    const normalizedEdges = normalizeWorkflowEdges(edges)
    set({
      nodes,
      edges: normalizedEdges,
      history: [{ nodes, edges: normalizedEdges }],
      historyIndex: 0,
    })
  },

  resetGraph: () => {
    set({
      nodes: DEFAULT_NODES,
      edges: [],
      history: [{ nodes: DEFAULT_NODES, edges: [] }],
      historyIndex: 0,
    })
  },

  updateNodeData: (nodeId, data) => {
    const state = get()
    if (state.readOnly) return
    const newNodes = state.nodes.map((node) =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
    )
    set({
      nodes: newNodes,
      // Don't save config changes to history (too frequent)
    })
  },

  updateEdgeData: (edgeId, data) => {
    const state = get()
    if (state.readOnly) return
    const newEdges = state.edges.map((edge) =>
      edge.id === edgeId ? { ...edge, data: { ...edge.data, ...data } } : edge
    )
    set({
      edges: newEdges,
    })
  },

  setActiveNode: (nodeId) => {
    set({
      nodes: get().nodes.map((node) => ({
        ...node,
        data: { ...node.data, active: node.id === nodeId },
      })),
    })
  },

  setReadOnly: (readOnly) => {
    set({ readOnly })
  },

  setWorkflowId: (workflowId) => {
    set({ workflowId })
  },

  setDropTargetLoopId: (loopId) => {
    set({ dropTargetLoopId: loopId })
  },

  setInvalidConnectionHighlight: (loopId, type) => {
    set({ invalidConnectionLoopId: loopId, invalidConnectionType: type })
  },

  undo: () => {
    const state = get()
    if (state.readOnly) return
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1
      const entry = state.history[newIndex]
      set({
        nodes: structuredClone(entry.nodes),
        edges: structuredClone(entry.edges),
        historyIndex: newIndex,
      })
    }
  },

  redo: () => {
    const state = get()
    if (state.readOnly) return
    if (state.historyIndex < state.history.length - 1) {
      const newIndex = state.historyIndex + 1
      const entry = state.history[newIndex]
      set({
        nodes: structuredClone(entry.nodes),
        edges: structuredClone(entry.edges),
        historyIndex: newIndex,
      })
    }
  },

  canUndo: () => {
    return get().historyIndex > 0
  },

  canRedo: () => {
    const state = get()
    return state.historyIndex < state.history.length - 1
  },

  // Test run execution actions
  startExecution: () => {
    set({
      isExecuting: true,
      executingNodeIds: [],
      completedNodeIds: [],
      executionOutput: "",
      executionError: null,
      executionActivities: [],
      nodeOutputs: {},
      loopProgress: {},
    })
  },

  setExecutingNode: (nodeId) => {
    if (!nodeId) {
      set({ executingNodeIds: [] })
      return
    }
    const state = get()
    // Guard against a stray / out-of-order activity un-completing a node that is
    // already terminal. A completed node may only return to the executing state
    // when it can legitimately re-run inside a loop; otherwise we leave it
    // completed (prevents spinner flicker on loop children & finished nodes).
    const isCompleted = state.completedNodeIds.includes(nodeId)
    if (isCompleted && !isLoopReExecutableNode(nodeId, state.nodes)) {
      return
    }
    const isAlreadyExecuting = state.executingNodeIds.includes(nodeId)
    const nextExecutingIds = isAlreadyExecuting
      ? state.executingNodeIds
      : [...state.executingNodeIds, nodeId]
    const nextCompletedIds = state.completedNodeIds.filter((id) => id !== nodeId)
    set({
      executingNodeIds: nextExecutingIds,
      completedNodeIds: nextCompletedIds,
    })
  },

  markNodeCompleted: (nodeId) => {
    const state = get()
    const nextExecutingIds = state.executingNodeIds.filter((id) => id !== nodeId)
    const nextCompletedIds = state.completedNodeIds.includes(nodeId)
      ? state.completedNodeIds
      : [...state.completedNodeIds, nodeId]
    set({
      executingNodeIds: nextExecutingIds,
      completedNodeIds: nextCompletedIds,
    })
  },

  appendExecutionOutput: (content) => {
    set({
      executionOutput: get().executionOutput + content,
    })
  },

  setExecutionError: (error) => {
    set({
      executionError: error,
      isExecuting: false,
      executingNodeIds: [],
    })
  },

  addExecutionActivity: (activity) => {
    set({
      executionActivities: [...get().executionActivities, activity],
    })
  },

  stopExecution: () => {
    const state = get()
    const newCompletedIds = Array.from(
      new Set([...state.completedNodeIds, ...state.executingNodeIds])
    )
    set({
      isExecuting: false,
      executingNodeIds: [],
      completedNodeIds: newCompletedIds,
    })
  },

  clearExecution: () => {
    set({
      isExecuting: false,
      executingNodeIds: [],
      completedNodeIds: [],
      executionOutput: "",
      executionError: null,
      executionActivities: [],
      nodeOutputs: {},
      loopProgress: {},
    })
  },

  // Node output tracking actions
  setNodeOutput: (nodeId, output) => {
    const state = get()
    if (!state.trackNodeOutputs) return
    set({
      nodeOutputs: { ...state.nodeOutputs, [nodeId]: output },
    })
  },

  setTrackNodeOutputs: (enabled) => {
    set({ trackNodeOutputs: enabled })
  },

  // Loop progress tracking actions
  setLoopProgress: (nodeId, current, total) => {
    set({
      loopProgress: { ...get().loopProgress, [nodeId]: { current, total } },
    })
  },

  // Test panel width action
  setTestPanelWidth: (width) => {
    set({ testPanelWidth: clampTestPanelWidth(width) })
  },
  // Auto-layout action
  requestAutoLayout: () => {
    set({ autoLayoutRequestId: get().autoLayoutRequestId + 1 })
  },
}))
